import React, { useEffect, useRef, useState } from 'react'
import { supabase } from '../supabaseClient'

function formatCountdown(ms) {
  if (ms <= 0) return '0s'
  const totalSeconds = Math.floor(ms / 1000)
  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  if (days > 0) return `${days}d ${hours}h ${minutes}m`
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`
  return `${minutes}m ${seconds}s`
}

function OutcomeBadge({ outcome }) {
  if (outcome === 'survived') return <span className="badge-green">Survived</span>
  if (outcome === 'eliminated') return <span className="badge-red">Out</span>
  if (outcome === 'void') return <span className="badge-gray">Void</span>
  return <span className="badge-gray">Pending</span>
}

export default function LMSPickScreen({ entry, round, authUser }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [gameweek, setGameweek] = useState(null)
  const [fixtures, setFixtures] = useState([])
  const [usedTeams, setUsedTeams] = useState(new Set())
  const [myPick, setMyPick] = useState(null)
  const [allPicks, setAllPicks] = useState(null)
  const [saving, setSaving] = useState(null)
  const [now, setNow] = useState(Date.now())
  const [savedToast, setSavedToast] = useState(null)
  const toastTimer = useRef(null)

  useEffect(() => {
    async function load() {
      setLoading(true)
      setError('')

      const { data: gwRows, error: gwError } = await supabase
        .from('lms_gameweeks')
        .select('*, lms_fixtures(*)')
        .eq('round_id', round.id)
        .neq('status', 'processed')
        .order('gameweek_number', { ascending: true })
        .limit(1)

      if (gwError) { setError(gwError.message); setLoading(false); return }

      const gw = gwRows?.[0] ?? null
      setGameweek(gw)
      setFixtures(
        (gw?.lms_fixtures ?? []).slice().sort((a, b) => new Date(a.kickoff_at) - new Date(b.kickoff_at))
      )

      if (!gw) { setLoading(false); return }

      const [{ data: usedData }, { data: pickData }] = await Promise.all([
        supabase.from('lms_used_teams').select('team_name').eq('entry_id', entry.id).eq('round_id', round.id),
        supabase.from('lms_picks').select('*').eq('entry_id', entry.id).eq('gameweek_id', gw.id).maybeSingle()
      ])

      setUsedTeams(new Set((usedData ?? []).map(u => u.team_name)))
      setMyPick(pickData ?? null)
      setLoading(false)
    }
    load()
  }, [entry.id, round.id])

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => () => clearTimeout(toastTimer.current), [])

  // deadline is null until the gameweek has fixtures (migration 007 derives
  // it from the earliest kickoff); new Date(null) silently coerces to epoch
  // 0, which would make this true immediately, so guard it explicitly.
  // Mirrors migration 013's RLS invariant exactly: editable/visible are
  // complements of (status, deadline), never both true, never both false.
  const noDeadlineYet = !gameweek || !gameweek.deadline
  const deadlineMs = noDeadlineYet ? null : new Date(gameweek.deadline).getTime()
  const lockedByStatus = !!gameweek && (gameweek.status === 'locked' || gameweek.status === 'processed')
  const pastDeadline = !noDeadlineYet && now >= deadlineMs
  const readOnly = lockedByStatus || pastDeadline
  const editable = !!gameweek && gameweek.status === 'upcoming' && !noDeadlineYet && now < deadlineMs

  useEffect(() => {
    if (!gameweek || !readOnly) return
    async function loadAllPicks() {
      const [{ data: picksData, error: picksError }, { data: namesData, error: namesError }] = await Promise.all([
        supabase.from('lms_picks').select('*').eq('gameweek_id', gameweek.id),
        supabase.rpc('get_lms_gameweek_pick_names', { p_gameweek_id: gameweek.id })
      ])

      if (picksError) { setError(picksError.message); return }
      if (namesError) { setError(namesError.message); return }

      const nameByEntry = new Map((namesData ?? []).map(n => [n.entry_id, n.display_name]))
      setAllPicks((picksData ?? []).map(p => ({ ...p, playerName: nameByEntry.get(p.entry_id) || 'Player' })))
    }
    loadAllPicks()
  }, [gameweek, readOnly])

  // Re-syncs gameweek state after a write comes back blocked. editable is
  // derived client-side from the same (status, deadline) inputs the RLS
  // policy checks, so the only way a write gets blocked is a race -- the
  // gameweek was locked, or the deadline passed, between load and click.
  async function refreshGameweekStatus() {
    const { data } = await supabase
      .from('lms_gameweeks')
      .select('*, lms_fixtures(*)')
      .eq('id', gameweek.id)
      .maybeSingle()
    if (data) {
      setGameweek(data)
      setFixtures((data.lms_fixtures ?? []).slice().sort((a, b) => new Date(a.kickoff_at) - new Date(b.kickoff_at)))
    }
  }

  async function pickTeam(fixture, team) {
    if (!editable || usedTeams.has(team) || saving) return
    setSaving(team)
    setError('')

    // maybeSingle(), not single(): an update blocked by RLS (gameweek
    // locked between load and click) matches zero rows and returns
    // successfully with no data -- single() would throw a raw "Cannot
    // coerce the result to a single JSON object" PostgREST error for that,
    // which is exactly the bug this fixes.
    const resp = myPick
      ? await supabase
          .from('lms_picks')
          .update({ team_picked: team, fixture_id: fixture.id })
          .eq('id', myPick.id)
          .select()
          .maybeSingle()
      : await supabase
          .from('lms_picks')
          .insert({ entry_id: entry.id, gameweek_id: gameweek.id, fixture_id: fixture.id, team_picked: team, outcome: 'pending' })
          .select()
          .maybeSingle()

    setSaving(null)

    if (resp.error) {
      // 42501 = row-level security violation -- the insert path's WITH CHECK
      // failing (unlike update, a blocked insert raises a real Postgres
      // error rather than just matching zero rows).
      setError(resp.error.code === '42501'
        ? 'This gameweek is locked and no longer accepting picks.'
        : resp.error.message)
      refreshGameweekStatus()
      return
    }
    if (!resp.data) {
      setError('This gameweek is locked and no longer accepting picks.')
      refreshGameweekStatus()
      return
    }
    setMyPick(resp.data)
    setSavedToast(team)
    clearTimeout(toastTimer.current)
    toastTimer.current = setTimeout(() => setSavedToast(null), 3000)
  }

  if (loading) return <div className="card"><p className="text-muted">Loading fixtures...</p></div>

  if (!gameweek) {
    return (
      <div className="card">
        <p className="text-muted">No gameweek is currently open for picks.</p>
      </div>
    )
  }

  const msRemaining = noDeadlineYet ? null : deadlineMs - now
  const urgent = !readOnly && !noDeadlineYet && msRemaining < 60 * 60 * 1000

  return (
    <div>
      {savedToast && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          background: 'var(--green-bg)', border: '1px solid var(--green-border)',
          borderRadius: 'var(--r-lg)', padding: '12px 16px', marginBottom: '16px'
        }}>
          <span style={{
            width: '20px', height: '20px', borderRadius: '50%', background: 'var(--green)',
            color: '#06210f', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '12px', fontWeight: '800', flexShrink: 0
          }}>✓</span>
          <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--green)' }}>
            Your pick is saved: {savedToast}
          </span>
        </div>
      )}

      {editable && !myPick && (
        <div style={{
          background: 'var(--amber-bg)', border: '1px solid var(--amber-border)',
          borderRadius: 'var(--r-lg)', padding: '12px 16px', marginBottom: '16px'
        }}>
          <p style={{ fontSize: '13px', fontWeight: '600', color: 'var(--amber)' }}>
            You haven't picked yet{!noDeadlineYet ? ` — deadline in ${formatCountdown(msRemaining)}` : ''}.
          </p>
        </div>
      )}

      <div className="card-gold card" style={{ marginBottom: '20px' }}>
        <div className="stat-label">{readOnly ? 'Picks locked' : noDeadlineYet ? 'Deadline' : 'Picks lock in'}</div>
        <div className="stat-num" style={{ fontSize: '28px', color: readOnly ? 'var(--text-3)' : urgent ? 'var(--red)' : 'var(--blue-bright)' }}>
          {readOnly ? 'Locked' : noDeadlineYet ? 'Not set yet' : formatCountdown(msRemaining)}
        </div>
        <div className="text-muted" style={{ fontSize: '12px', marginTop: '4px' }}>
          {gameweek.deadline
            ? `Deadline: ${new Date(gameweek.deadline).toLocaleString()}`
            : readOnly ? 'Locked by admin.' : 'Set automatically once fixtures are added for this gameweek.'}
        </div>
      </div>

      <div className="card">
        <div style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: '12px' }}>
          Gameweek {gameweek.gameweek_number} fixtures
        </div>

        {fixtures.length === 0 && <p className="text-muted">No fixtures loaded for this gameweek yet.</p>}

        {fixtures.map(fix => {
          const pickedThisFixture = myPick?.fixture_id === fix.id
          return (
            <div
              key={fix.id}
              style={{
                borderBottom: '1px solid var(--border)',
                background: pickedThisFixture ? 'var(--green-bg)' : 'transparent',
                borderRadius: pickedThisFixture ? 'var(--r-sm)' : '0',
                margin: pickedThisFixture ? '0 -12px' : '0',
                padding: pickedThisFixture ? '10px 12px' : '10px 0'
              }}
            >
              <div style={{ fontSize: '11px', color: 'var(--text-3)', marginBottom: '6px' }}>
                {fix.kickoff_at ? new Date(fix.kickoff_at).toLocaleString() : 'Kickoff TBC'}
                {fix.postponed && <span className="badge-gray" style={{ marginLeft: '8px' }}>Postponed</span>}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                {[fix.home_team, fix.away_team].map(team => {
                  const used = usedTeams.has(team)
                  const selected = myPick?.team_picked === team
                  const teamStyle = {
                    flex: 1,
                    padding: '12px 12px',
                    borderRadius: 'var(--r)',
                    fontFamily: 'var(--ff-b)',
                    fontSize: '13px',
                    fontWeight: selected ? '700' : '500',
                    border: selected ? '1px solid var(--green-border)' : '1px solid var(--border-strong)',
                    background: selected ? 'var(--green-bg)' : 'var(--bg)',
                    color: used ? 'var(--text-3)' : 'var(--text)',
                    opacity: used ? 0.5 : 1,
                    textDecoration: used ? 'line-through' : 'none'
                  }

                  // Read-only whenever not editable: no button, no click
                  // handler, nothing that implies this can still change.
                  if (!editable) {
                    return (
                      <div key={team} style={{ ...teamStyle, textAlign: 'center' }}>
                        {team}
                      </div>
                    )
                  }

                  return (
                    <button
                      key={team}
                      disabled={used || !!saving}
                      onClick={() => pickTeam(fix, team)}
                      style={{ ...teamStyle, cursor: used ? 'not-allowed' : 'pointer' }}
                    >
                      {saving === team ? 'Saving...' : team}
                    </button>
                  )
                })}
              </div>
              {pickedThisFixture && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '10px' }}>
                  <span className="badge-green" style={{ flexShrink: 0, marginTop: '2px' }}>{readOnly ? 'Final pick' : 'Locked in'}</span>
                  <span style={{ fontSize: '12px', lineHeight: '1.5', color: 'var(--text-2)' }}>
                    {readOnly
                      ? `Your pick for this gameweek: ${myPick.team_picked}.`
                      : `You've picked ${myPick.team_picked}. You can change this until the deadline above.`}
                  </span>
                </div>
              )}
            </div>
          )
        })}

        {myPick?.is_auto_pick && (
          <p className="text-muted" style={{ fontSize: '12px', marginTop: '12px' }}>
            <span className="badge-gray">Auto</span> This pick was made automatically at the deadline.
          </p>
        )}
      </div>

      {readOnly && (
        <div className="card">
          <div style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: '12px' }}>
            All picks — Gameweek {gameweek.gameweek_number}
          </div>

          {allPicks === null && <p className="text-muted">Loading picks...</p>}

          {allPicks && allPicks.length === 0 && <p className="text-muted">No picks were made this gameweek.</p>}

          {allPicks && allPicks.map(p => {
            const isMe = p.entry_id === entry.id
            const name = p.playerName
            return (
              <div
                key={p.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px 12px',
                  margin: '0 -12px',
                  borderBottom: '1px solid var(--border)',
                  background: isMe ? 'var(--blue-dim)' : 'transparent',
                  borderRadius: isMe ? 'var(--r-sm)' : '0'
                }}
              >
                <span style={{ fontSize: '13px', fontWeight: isMe ? '700' : '500' }}>
                  {name}{isMe ? ' (you)' : ''}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '13px' }}>{p.team_picked}</span>
                  {p.is_auto_pick && <span className="badge-gray">Auto</span>}
                  <OutcomeBadge outcome={p.outcome} />
                </div>
              </div>
            )
          })}
        </div>
      )}

      {error && (
        <p style={{ color: 'var(--red)', fontSize: '13px', marginTop: '12px' }}>{error}</p>
      )}
    </div>
  )
}
